/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-param-reassign */
/* eslint-disable no-else-return */
import { CheckIcon } from '@phosphor-icons/react';
import React, {
  useRef, useState, useEffect, KeyboardEvent, useCallback, forwardRef, useImperativeHandle
} from 'react';
import { Typography } from 'tfc-components';

import { DynamicIcon } from '../ActionIconList';

import Icon from 'components/atoms/Icon';
import useClickOutside from 'hooks/useClickOutside';
import { AiAssistantServiceTypes } from 'services/accountConversations/types';
import mapModifiers, { getTextFromHTML } from 'utils/functions';

export interface DynamicTextareaRef {
  insertOption: (option: AiAssistantServiceTypes) => void;
  resetPhone: () => void;
  focus: () => void;
  fillText: (text: string) => void;
}

interface DynamicTextareaProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit?: (e: KeyboardEvent<HTMLDivElement>) => void;
  onFocus?: () => void;
  confirmType?: string;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  options?: AiAssistantServiceTypes[];
  showOptions?: boolean;
  onShowOptions?: (show: boolean) => void;
  selectedOptionIndex?: number;
  onChangeSelectedOptionIndex?: (index: number) => void;
  activeOptionIndex?: number;
  onChangeActiveOptionIndex?: (index: number) => void;
  disableEnter?: boolean;
}

const DynamicTextarea = forwardRef<DynamicTextareaRef, DynamicTextareaProps>(
  (
    {
      value,
      onChange,
      onSubmit,
      onFocus,
      confirmType,
      disabled = false,
      placeholder = '',
      className = '',
      options = [],
      showOptions = false,
      onShowOptions,
      selectedOptionIndex = 0,
      onChangeSelectedOptionIndex,
      activeOptionIndex,
      onChangeActiveOptionIndex,
      disableEnter,
    },
    ref
  ) => {
    const phoneRegex = /^0\d{0,9}$/;
    const [phoneNumber, setPhoneNumber] = useState('');
    const [isComposing, setIsComposing] = useState(false);
    const [isSubmited, setIsSubmited] = useState(false);
    const [option, setOption] = useState(options || []);
    const editorRef = useRef<HTMLDivElement>(null);
    const optionsRef = useRef<HTMLDivElement>(null);

    useClickOutside(optionsRef, () => {
      onShowOptions?.(false);
    });

    useEffect(() => {
      if (editorRef.current && editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value;
      }
    }, [value]);

    // eslint-disable-next-line consistent-return
    useEffect(() => {
      const makeSpansUnselectable = () => {
        if (editorRef.current) {
          const spans = editorRef.current.querySelectorAll('span');
          spans.forEach((span) => {
            span.contentEditable = 'false';
            span.style.userSelect = 'none';
            span.style.cursor = 'default';
            span.style.display = 'inline-block';
          });
        }
      };

      const handleMutations = (mutations: MutationRecord[]) => {
        makeSpansUnselectable();

        // Check for removed spans but don't use a variable that's referenced in the loop
        let spanWasRemoved = false;
        let addingSpan = false;

        // First check if we're adding any span nodes
        for (const mutation of mutations) {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            for (let i = 0; i < mutation.addedNodes.length; i += 1) {
              const node = mutation.addedNodes[i];
              if (
                node.nodeName === 'SPAN'
                || (node.nodeType === Node.ELEMENT_NODE && (node as Element).querySelector('span'))
              ) {
                addingSpan = true;
                break;
              }
            }
          }
        }

        // Then check for removed spans
        for (const mutation of mutations) {
          if (mutation.type === 'childList' && mutation.removedNodes.length > 0) {
            for (let i = 0; i < mutation.removedNodes.length; i += 1) {
              const node = mutation.removedNodes[i];
              if (
                node.nodeName === 'SPAN'
                || (node.nodeType === Node.ELEMENT_NODE && (node as Element).querySelector('span'))
              ) {
                spanWasRemoved = true;
                break;
              }
            }
          }
        }

        // Only reset selected option index if spans were removed AND we're not adding a new span
        if (spanWasRemoved && !addingSpan) {
          // Check if there are any spans still in the editor
          const remainingSpans = editorRef.current?.querySelectorAll('span').length || 0;

          // Only reset if there are no spans left
          if (remainingSpans === 0) {
            // onChangeSelectedOptionIndex?.(-1);
            onChangeActiveOptionIndex?.(-1);
            // Also hide options panel if it's showing
            onShowOptions?.(false);
          }
        }
      };

      if (editorRef.current) {
        // Initial setup
        makeSpansUnselectable();

        // Create MutationObserver to handle spans
        const observer = new MutationObserver(handleMutations);
        observer.observe(editorRef.current, {
          childList: true,
          subtree: true,
          attributes: false,
          characterData: false,
        });

        return () => observer.disconnect();
      }
    }, [onChangeSelectedOptionIndex, onShowOptions]);

    const validateAndFormatInput = (input: string) => {
      // Remove any non-digit characters
      const digitsOnly = input.replace(/\D/g, '');

      // Check if the input matches our pattern and doesn't exceed max length
      if (phoneRegex.test(digitsOnly) && digitsOnly.length <= 10) {
        setPhoneNumber(digitsOnly);
        return digitsOnly;
      } else if (digitsOnly === '') {
        // Empty is allowed
        setPhoneNumber('');
        return '';
      } else if (!digitsOnly.startsWith('0')) {
        // Must start with 0
        return phoneNumber; // Keep previous valid value
      } else {
        // Too long or invalid format
        // If input starts with 0 but is too long, truncate it
        if (digitsOnly.startsWith('0') && digitsOnly.length > 10) {
          const truncated = digitsOnly.slice(0, 10);
          setPhoneNumber(truncated);
          return truncated;
        }
        return phoneNumber; // Keep previous valid value
      }
    };

    const flattenHTML = () => {
      if (!editorRef.current) return;
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const currentNode = range.startContainer;
        const currentPos = range.startOffset;
        const hasSpans = editorRef.current.querySelectorAll('span').length > 0;

        if (hasSpans) {
          // Save the cursor position as character offset from start
          let cursorOffset = 0;
          const allNodes = document.createTreeWalker(editorRef.current, NodeFilter.SHOW_TEXT, null);

          let node;
          while ((node = allNodes.nextNode())) {
            if (node === currentNode) {
              cursorOffset += currentPos;
              break;
            } else {
              cursorOffset += (node.textContent || '').length;
            }
          }

          // Flatten all HTML to plain text
          const plainText = editorRef.current.innerText;
          editorRef.current.innerHTML = plainText;

          // Restore cursor position after the "/"
          if (cursorOffset > 0) {
            const newTextNode = editorRef.current.firstChild;
            if (newTextNode && selection) {
              const newRange = document.createRange();
              const offset = Math.min(cursorOffset, (newTextNode.textContent || '').length);
              newRange.setStart(newTextNode, offset);
              newRange.collapse(true);
              selection.removeAllRanges();
              selection.addRange(newRange);
            }
          }
        }
      }
    };

    const handleInput = useCallback(
      (e: any) => {
        if (editorRef.current) {
          const rawInput = e.currentTarget.textContent || '';
          if (editorRef.current.textContent === '') setIsComposing(false);

          if (confirmType === 'enterPhone') {
            const validatedInput = validateAndFormatInput(rawInput);

            if (validatedInput !== rawInput) {
              e.preventDefault();
            }
          }

          const selection = window.getSelection();
          if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            const currentNode = range.startContainer;
            const currentPos = range.startOffset;

            // Check if text node and current character is "/"
            if (currentNode.nodeType === Node.TEXT_NODE && currentPos > 0) {
              const textContent = currentNode.textContent || '';
              // Check if current character is "/" and previous is space
              if (rawInput?.startsWith('/')) {
                onShowOptions?.(true);
                setOption(options);
                if (rawInput?.replace('/', '')) {
                  const matchValue = options.filter((comman) => comman?.serviceData?.action?.replace('ai-', '')?.toLowerCase().includes(rawInput?.replace('/', '').toLowerCase()));
                  if (matchValue.length > 0) {
                    setOption(matchValue);
                  } else {
                    onShowOptions?.(false);
                  }
                }
                // onChangeSelectedOptionIndex?.(-1);
                return;
              }
            }

            onShowOptions?.(false);
          }

          // Update value
          onChange(editorRef.current.innerHTML);
        }
      },
      [onChange, isComposing, confirmType]
    );

    const insertOptionAtCursor = useCallback(
      (option: AiAssistantServiceTypes) => {
        if (!editorRef.current) return;

        const editor = editorRef.current;
        editor.focus();

        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0);

        // 1. Xoá tất cả nội dung trước con trỏ
        const preRange = document.createRange();
        preRange.setStart(editor, 0);
        preRange.setEnd(range.startContainer, range.startOffset);
        preRange.deleteContents();

        // 2. Tạo span
        const span = document.createElement('span');
        span.className = 'o-dynamicTextarea_spanOption';
        span.style.fontWeight = 'bold';
        span.contentEditable = 'false';
        span.style.userSelect = 'none';
        span.style.cursor = 'default';
        span.style.display = 'inline-block';
        span.textContent = option.serviceData.title;

        // 3. Insert span + space
        const space = document.createTextNode('\u00A0'); // non-breaking space
        const frag = document.createDocumentFragment();
        frag.appendChild(span);
        frag.appendChild(space);
        range.insertNode(frag);

        // 4. Move con trỏ sau space
        const newRange = document.createRange();
        newRange.setStartAfter(space);
        newRange.collapse(true);
        selection.removeAllRanges();
        selection.addRange(newRange);

        // 5. Update value
        onChange(editor.innerHTML);
      },
      [onChange]
    );

    const insertOption = useCallback(
      (option: AiAssistantServiceTypes) => {
        if (editorRef.current) {
          const selection = window.getSelection();
          if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);

            // Xoá ký tự slash trước đó
            range.setStart(range.startContainer, range.startOffset - 1);
            range.deleteContents();

            // Xoá toàn bộ nội dung trước span trong editor
            editorRef.current.innerHTML = '';

            // Tạo span
            const span = document.createElement('span');
            span.className = 'o-dynamicTextarea_spanOption';
            span.contentEditable = 'false';
            span.style.userSelect = 'none';
            span.style.cursor = 'default';
            span.style.display = 'inline-block';
            span.textContent = option.serviceData.title;

            // Insert span
            range.insertNode(span);

            // Thêm khoảng trắng và đặt con trỏ sau nó
            const space = document.createTextNode('\u00A0');
            span.after(space);

            // Move cursor sau khoảng trắng
            const newRange = document.createRange();
            newRange.setStartAfter(space);
            newRange.collapse(true);

            selection.removeAllRanges();
            selection.addRange(newRange);

            // Update value
            onChange(editorRef.current.innerHTML);
            onShowOptions?.(false);
          }
        }
      },
      [onChange, onShowOptions]
    );

    const handleKeyDown = useCallback(
      (e: KeyboardEvent<HTMLDivElement>) => {
        if (confirmType === 'enterPhone') {
          const isDigit = /^\d$/.test(e.key);
          const isControlKey = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'].includes(e.key);

          // Block letters and special characters (allow only digits and control keys)
          if (!isDigit && !isControlKey) {
            e.preventDefault();
            return;
          }
          // Special handling for first digit - must be 0
          if (phoneNumber === '' && e.key !== '0' && /^\d$/.test(e.key)) {
            e.preventDefault();
          }

          // Prevent adding more digits if already at max length
          if (phoneNumber.length >= 10 && /^\d$/.test(e.key)) {
            e.preventDefault();
          }
        }

        // Submit
        if (e.key === 'Enter' && !e.shiftKey && !showOptions && editorRef.current && getTextFromHTML(editorRef.current?.innerHTML) !== '' && !disableEnter) {
          e.preventDefault();
          onSubmit?.(e);
          setPhoneNumber('');
          setIsSubmited(true);
        }

        if (showOptions) {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            const newIndex = selectedOptionIndex < option.length - 1
              ? selectedOptionIndex + 1 : selectedOptionIndex;
            onChangeSelectedOptionIndex?.(newIndex);

            // Scroll the newly selected option into view
            setTimeout(() => {
              const selectedElement = document.querySelector(
                '.o-dynamicTextarea_options_item.o-dynamicTextarea_options_item-selected'
              );
              selectedElement?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }, 0);
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            const newIndex = selectedOptionIndex > 0
              ? selectedOptionIndex - 1 : selectedOptionIndex;
            onChangeSelectedOptionIndex?.(newIndex);

            // Scroll the newly selected option into view
            setTimeout(() => {
              const selectedElement = document.querySelector(
                '.o-dynamicTextarea_options_item.o-dynamicTextarea_options_item-selected'
              );
              selectedElement?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }, 0);
          } else if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault();
            flattenHTML();
            insertOption(option[selectedOptionIndex]);
            const optionsIndex = options.findIndex((ops) => ops.serviceData?.code === option[selectedOptionIndex].serviceData?.code);
            onChangeActiveOptionIndex?.(optionsIndex);
          } else if (e.key === 'Escape') {
            e.preventDefault();
            onShowOptions?.(false);
          }
        } else if (e.key === 'Tab') {
          e.preventDefault();
          document.execCommand('insertText', false, '    ');
        }
      },
      [showOptions, selectedOptionIndex, option, insertOption]
    );

    const handleOptionClick = (option: AiAssistantServiceTypes) => {
      flattenHTML();
      insertOptionAtCursor(option);
      onChangeActiveOptionIndex?.(options.findIndex(
        (o) => o.serviceData.code === option.serviceData.code
      ));
      editorRef.current?.focus();
      onShowOptions?.(false);
    };

    useImperativeHandle(ref, () => ({
      insertOption: (option: AiAssistantServiceTypes) => {
        flattenHTML();
        insertOptionAtCursor(option);
      },
      resetPhone: () => {
        setPhoneNumber('');
      },
      focus: () => {
        editorRef.current?.focus();
      },
      fillText: (text: string) => {
        if (editorRef.current) {
          editorRef.current.innerText = text; // hoặc innerHTML nếu muốn giữ format
          onChange(editorRef.current.innerHTML);
        }
      },
    }));

    useEffect(() => {
      const timeoutId = setTimeout(() => {
        if (editorRef.current && isSubmited) {
          editorRef.current.textContent = '';
          setIsSubmited(false);
        }
      }, 0);
      return () => {
        clearTimeout(timeoutId);
      };
    }, [isSubmited]);
    return (
      <>
        <div className={mapModifiers('o-dynamicTextarea_options', showOptions && 'show')} ref={optionsRef}>
          {option.map((opt, index) => (
            <button
              type="button"
              key={opt.serviceData.code}
              className={mapModifiers('o-dynamicTextarea_options_item', selectedOptionIndex === index && 'selected')}
              onClick={() => handleOptionClick(opt)}
            >
              {
                opt.serviceData.icon
                  ? <DynamicIcon className="o-dynamicTextarea_options_item_icon" name={opt.serviceData.icon} size={24} />
                  : <Icon iconName="fileOutline" size="24" className="o-dynamicTextarea_options_item_icon" />

              }
              <div className="o-dynamicTextarea_options_item_text">
                <div className="o-dynamicTextarea_options_item_heading_wrapper">
                  <Typography.Text fontweight="600" extendClasses="o-dynamicTextarea_options_item_heading">
                    /
                    {opt.serviceData?.actionName}
                  </Typography.Text>
                  {activeOptionIndex === index && <CheckIcon size={24} className="o-dynamicTextarea_options_item_check" />}
                </div>
                <Typography.Text extendClasses="o-dynamicTextarea_options_item_title">
                  {opt.serviceData?.title}
                </Typography.Text>
                <Typography.Text extendClasses="o-dynamicTextarea_options_item_desc">
                  {opt.serviceData.description}
                </Typography.Text>
              </div>
            </button>
          ))}
        </div>

        <div className={`o-dynamicTextarea ${className}`}>
          {(!editorRef.current || !editorRef.current.textContent) && (
            <div className="o-dynamicTextarea_placeholder">{placeholder}</div>
          )}
          <div
            ref={editorRef}
            contentEditable={!disabled}
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            onCompositionStart={() => setIsComposing(true)}
            onCompositionEnd={(e) => {
              setIsComposing(false);
              handleInput(e);
            }}
            onPaste={(e) => {
              e.preventDefault();
              const text = e.clipboardData.getData('text/plain');
              document.execCommand('insertText', false, text);
            }}
            style={{
              outline: 'none',
              width: '100%',
              wordBreak: 'break-word',
            }}
            onFocus={onFocus}
            role="textbox"
            aria-multiline="true"
          />
        </div>
      </>
    );
  }
);

export default DynamicTextarea;
